import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearch } from "wouter";
import {
  CLIENT_REPORT_STATUSES,
  CLIENT_REPORT_STATUS_MEANINGS,
  type ClientReportCategory,
  type ClientReportStatus,
} from "@titleverify/shared";
import { apiErrorText, apiFetch, type AdminClientReport } from "../api.js";
import { useAuth } from "../auth.js";
 import { AdminBreadcrumb } from "./AdminHome.js";

/**
 * THE CLIENT REPORTS QUEUE.
 *
 * ===========================================================================
 * NOTHING ON THIS SCREEN IS A FINDING ABOUT A PROFESSIONAL.
 * ===========================================================================
 *
 * Every report here is a thing A CLIENT SAID, attributed to them. Every decision
 * an admin takes here is a thing THE PLATFORM DECIDED ABOUT ITS OWN ROUTING.
 * There is no third kind of fact available on this page, because no honest one
 * exists: whether an attorney-at-law has committed professional misconduct is
 * for the Bar Association of Sri Lanka and the Supreme Court, which alone may
 * disenrol. Asserting it here would be defamation exposure and the conduct
 * equivalent of rendering a legal conclusion, which CLAUDE.md non-negotiable 2
 * forbids in the title domain for identical reasons.
 *
 * Hence the two verbs. "Uphold" does NOT mean the allegation is proved — it
 * means the platform accepts the report as a basis for its own decisions about
 * where to send work. "Dismiss" is not a finding of innocence. Both meanings are
 * rendered from CLIENT_REPORT_STATUS_MEANINGS, never restated in words chosen
 * here, because the risk this whole feature turns on is a screen reaching for a
 * stronger word than the data supports.
 *
 * DECIDING SOMETHING HERE DOES NOTHING TO ANYBODY. Upholding stops no assignment
 * and unlists nobody. If an admin decides to stop routing work to someone, that
 * is the separate deliberate act on the roster. This queue informs a human; it
 * does not act.
 *
 * Handling happens BY TELEPHONE, outside this system, on purpose — there is no
 * in-app messaging and none is wanted. This screen is the record of what was
 * said and what we decided, not the forum in which it is argued out.
 */

const CATEGORY_COPY: Record<ClientReportCategory, string> = {
  WORK_NOT_COMPLETED: "The client says the work has not been delivered",
  UNRESPONSIVE: "The client says they have not been able to get a reply",
  MISSED_APPOINTMENT: "The client says an arranged meeting did not happen",
  FEE_DISPUTE: "The client disputes what they were asked to pay",
  CONDUCT_CONCERN: "The client raised a concern about how they were treated",
  OTHER: "Something else — see the client's own words",
};

export function AdminReports() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  // Linked from the roster as ?aboutUserId=… — "show me everything filed about
  // this one person", which is the history an admin actually reads before
  // deciding anything.
  const aboutUserId = new URLSearchParams(useSearch()).get("aboutUserId");
  const [status, setStatus] = useState<ClientReportStatus | "">("open");

  const query = new URLSearchParams();
  if (status) query.set("status", status);
  if (aboutUserId) query.set("aboutUserId", aboutUserId);

  const { data, error, isPending } = useQuery({
    queryKey: ["admin", "reports", { status, aboutUserId }],
    queryFn: async () =>
      apiFetch<{ reports: AdminClientReport[] }>(`/admin/reports?${query.toString()}`, {
        token: await getToken(),
      }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "reports"] });
  };

  return (
    <section className="wide">
      <AdminBreadcrumb />
      <header className="page-head">
        <p className="eyebrow">Reports queue</p>
        <h2>Client reports</h2>
        <p className="lede">
          What clients have told us about how their cases were handled. Each one is{" "}
          <strong>that client's account</strong>, recorded as theirs. The platform has not checked
          any of it and does not decide whether it happened.
        </p>
      </header>

      <div className="notice">
        <p>
          Handling is by telephone: ring the client, ring the professional, and decide. Deciding
          here records what <em>the platform</em> will do about its own routing. It does not remove
          anybody and has no automatic effect — if you decide to stop sending someone work, that is
          a separate act on the roster.
        </p>
      </div>

      {aboutUserId ? (
        <p className="caveat">
          Filtered to every report about one professional (<code>{aboutUserId}</code>) — the
          history behind a roster count. <Link href="/admin/reports">Show all reports</Link>
        </p>
      ) : null}

      <div className="field filter">
        <label htmlFor="status">Show</label>
        <select
          id="status"
          value={status}
          onChange={(e) => setStatus(e.target.value as ClientReportStatus | "")}
        >
          <option value="open">Awaiting review</option>
          {CLIENT_REPORT_STATUSES.filter((s) => s !== "open").map((s) => (
            <option key={s} value={s}>
              Already decided: {s}
            </option>
          ))}
          <option value="">Everything</option>
        </select>
      </div>

      {isPending ? <p className="muted" role="status">Loading reports…</p> : null}
      {error ? <p role="alert">{apiErrorText(error, "Could not load the client reports.")}</p> : null}

      {data ? (
        data.reports.length === 0 ? (
          <p className="empty">
            {/* An honest empty state: nothing matched, which is a fact rather than a failure. */}
            No reports match this filter.
          </p>
        ) : (
          <ul className="report-list">
            {data.reports.map((report) => (
              <ReportCard key={report.id} report={report} onDecided={invalidate} />
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

function ReportCard({
  report,
  onDecided,
}: {
  report: AdminClientReport;
  onDecided: () => void;
}) {
  const { getToken } = useAuth();
  const [note, setNote] = useState("");

  const review = useMutation({
    mutationFn: async (outcome: "dismiss" | "uphold") =>
      apiFetch<AdminClientReport>(`/admin/reports/${report.id}/review`, {
        token: await getToken(),
        method: "POST",
        body: { outcome, note: note.trim() || null },
      }),
    onSuccess: onDecided,
  });

  const decided = report.status !== "open";

  return (
    <li className="report-card">
      <dl>
        <dt>Filed</dt>
        <dd>{new Date(report.createdAt).toLocaleString()}</dd>
        <dt>By (client)</dt>
        <dd>
          <code>{report.reportedByUserId}</code>
        </dd>
        <dt>About (professional)</dt>
        <dd>
          <code>{report.aboutUserId}</code>
        </dd>
        <dt>On case</dt>
        <dd>
          <code>{report.caseId}</code>
        </dd>
        <dt>What the client reported</dt>
        <dd>{CATEGORY_COPY[report.category]}</dd>
        <dt>In the client's own words</dt>
        <dd>
          {report.detail ? (
            /*
              Attributed, always. The quotation marks and the label are doing
              real work: this is the client's account of events, not the
              platform's description of the professional, and it must never be
              readable as the latter.
            */
            <blockquote>“{report.detail}”</blockquote>
          ) : (
            <span className="muted">They chose the category and wrote nothing further.</span>
          )}
        </dd>
      </dl>

      {decided ? (
        <div className="decided">
          <p>
            {/*
              The decision word gets .state, not .status-*: "upheld" is a fact
              about the platform's own routing, and the verification palette
              would colour it as a finding about the professional.
            */}
            <strong>Decided:</strong> <span className="state">{report.status}</span>
          </p>
          {/* The server's wording, verbatim. Never re-derived — see the file comment. */}
          <p>{report.statusMeaning}</p>
          <p>
            <small>
              Decided {report.reviewedAt ? new Date(report.reviewedAt).toLocaleString() : "—"}. A
              decision is not revisited in place; if the platform changes its mind that is a new
              decision with its own record.
            </small>
          </p>
          {report.reviewNote ? (
            <p>
              <small>Note recorded at the time: {report.reviewNote}</small>
            </p>
          ) : null}
        </div>
      ) : (
        <div className="stack">
          <div className="field">
            <label htmlFor={`note-${report.id}`}>
              Note on the platform's reasoning (optional)
            </label>
            <textarea
              id={`note-${report.id}`}
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. spoke to the client 19 July; not routing further work pending a call with the professional"
            />
            <small className="field-hint">
              Write about <em>our</em> reasoning, not about the professional. This is a record of a
              commercial decision we made, and it is not the place to characterise anybody.
            </small>
          </div>

          {review.error ? (
            <p role="alert">{apiErrorText(review.error, "Could not record the decision.")}</p>
          ) : null}

          {/*
            Both buttons state what the platform is deciding, in full. Neither is
            labelled with a bare "Uphold" / "Dismiss": those words carry an
            ordinary meaning this decision does not have.

            The two meanings sit ABOVE the buttons, in a .consequence panel. They
            are the server's own definitions of what each decision is, and the
            point of them is to be read before the decision rather than found
            underneath it afterwards. Nothing here is reworded; both strings come
            from CLIENT_REPORT_STATUS_MEANINGS.
          */}
          <div className="consequence">
            <p>
              <small>
                <strong>Taking it into account</strong> —{" "}
                {CLIENT_REPORT_STATUS_MEANINGS.upheld}
              </small>
            </p>
            <p>
              <small>
                <strong>Not taking it into account</strong> —{" "}
                {CLIENT_REPORT_STATUS_MEANINGS.dismissed}
              </small>
            </p>
          </div>

          <div className="row">
            <button
              type="button"
              disabled={review.isPending}
              onClick={() => review.mutate("uphold")}
            >
              Take this into account when routing work
            </button>
            <button
              type="button"
              className="quiet"
              disabled={review.isPending}
              onClick={() => review.mutate("dismiss")}
            >
              Do not take this into account
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
